import { Grid } from '@material-ui/core';
import type { Entity } from '@backstage/catalog-model';
import {
  EntityHasSubcomponentsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import { SystemAboutCard } from './SystemAboutCard'
import { SystemApisCard } from './SystemApisCards';
import { customAPIPage } from './CustomAPIPage';
import { GraphQLAPIPage } from './GraphQLAPIPage';
import { TabbedLayout } from '@backstage/core-components';
import { Content } from '@backstage/core-components';
import { Box } from '@material-ui/core';
import { CustomerEntityBanner } from './CustomEntityBanner';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      {/* <EntityAboutCard variant="gridItem" /> */}
      <SystemAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <TabbedLayout>
    <TabbedLayout.Route path="/" title="Overview">
      {overviewContent}
    </TabbedLayout.Route>

    <TabbedLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </TabbedLayout.Route>
  </TabbedLayout>
);

const systemPage = (
  <Box style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    <CustomerEntityBanner />

    {/* Scroll container goes here */}
    <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Content>
        <TabbedLayout>
          <TabbedLayout.Route path="/" title="Overview">
            <Grid container spacing={3} alignItems="stretch">
              {entityWarningContent}
              <Grid item md={12}>
                <SystemAboutCard />
              </Grid>
              <Grid item md={12}>
                <SystemApisCard />
              </Grid>
              <Grid item md={12} xs={12}>
                <EntityCatalogGraphCard variant="gridItem" height={400} />
              </Grid>
            </Grid>
          </TabbedLayout.Route>

          <TabbedLayout.Route path="/diagram" title="Diagram">
            <EntityCatalogGraphCard
              variant="gridItem"
              direction={Direction.TOP_BOTTOM}
              title="System Diagram"
              height={700}
              relations={[
                RELATION_PART_OF,
                RELATION_HAS_PART,
                RELATION_API_CONSUMED_BY,
                RELATION_API_PROVIDED_BY,
                RELATION_CONSUMES_API,
                RELATION_PROVIDES_API,
                RELATION_DEPENDENCY_OF,
                RELATION_DEPENDS_ON,
              ]}
              unidirectional={false}
            />
          </TabbedLayout.Route>
        </TabbedLayout>
      </Content>
    </Box>
  </Box>  
);


// const systemPage = (
//   <EntityLayout>
//     <EntityLayout.Route path="/" title="Overview">
//       <Content>
//         <TabbedLayout>
//           <TabbedLayout.Route path="/" title="Overview">
//             <Grid container spacing={3} alignItems="stretch">
//               {entityWarningContent}
//               <Grid item md={12}>
//                 <SystemAboutCard />
//               </Grid>
//               <Grid item md={12}>
//                 <SystemApisCard />
//               </Grid>
//               <Grid item md={12} xs={12}>
//                 <EntityCatalogGraphCard variant="gridItem" height={400} />
//               </Grid>
//             </Grid>
//           </TabbedLayout.Route>
//           <TabbedLayout.Route path="/diagram" title="Diagram">
//             <EntityCatalogGraphCard
//               variant="gridItem"
//               direction={Direction.TOP_BOTTOM}
//               title="System Diagram"
//               height={700}
//               relations={[
//                 RELATION_PART_OF,
//                 RELATION_HAS_PART,
//                 RELATION_API_CONSUMED_BY,
//                 RELATION_API_PROVIDED_BY,
//                 RELATION_CONSUMES_API,
//                 RELATION_PROVIDES_API,
//                 RELATION_DEPENDENCY_OF,
//                 RELATION_DEPENDS_ON,
//               ]}
//               unidirectional={false}
//             />
//           </TabbedLayout.Route>
//         </TabbedLayout>
//       </Content>
//     </EntityLayout.Route>
//   </EntityLayout>
// );



const isGraphQLApi = (entity: Entity) =>
  (entity?.spec?.type as string | undefined)?.toLowerCase() === 'graphql';

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={e => isKind('api')(e) && isGraphQLApi(e)} children={<GraphQLAPIPage />} />
    <EntitySwitch.Case if={isKind('api')} children={customAPIPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
